import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs'
import PageLayout from '../../../shared/ui/layouts/PageLayout/PageLayout'
import { TimeLineWidget, TimeLineWeekWidget, TimelineNav, useTimelineDay } from '../../../widgets/timeline'
import { DraftTaskPanel, useDispositionDraftStore, DispositionConfigProvider } from '../../../features/disposition'
import type { DefaultDurations } from '../../../features/disposition'
import { useTimelineView } from '../../../features/timeline-navigation'
import { isMidnightScheduledDate } from '../../../shared/lib/timeline/dateHelpers'
import { TASK_COLORS } from '../../../shared/lib/timelineColors'
import { useTaskDetail, useUpdateTask } from '../../../entities/task'
import { routes } from '../../../shared/config/routes'
import { SuccessDialog } from '../../../shared/ui/feedback/SuccessDialog/SuccessDialog'
import { useSuccessDialog } from '../../../shared/hooks/useSuccessDialog'

const DEFAULT_DURATIONS: DefaultDurations = {
  plannedTravelDuration: 30,
  plannedWorkDuration: 60,
  plannedDepartureDuration: 30,
}

export const TaskPlanPage = () => {
  const navigate = useNavigate()
  const [taskId] = useQueryState('taskId', parseAsInteger.withDefault(0))
  const [dayKey, setDayKey] = useQueryState('day', parseAsString.withDefault(''))

  const { view, setView } = useTimelineView()
  useTimelineDay()

  const { data: task } = useTaskDetail(taskId)
  const updateTask = useUpdateTask(taskId)
  const { showSuccess, dialogProps } = useSuccessDialog()

  const finalizeDraft = useDispositionDraftStore((s) => s.finalizeDraft)
  const setGhost = useDispositionDraftStore((s) => s.setGhost)
  const setDurations = useDispositionDraftStore((s) => s.setDurations)
  const draft = useDispositionDraftStore((s) => s.draft)
  const clearAll = useDispositionDraftStore((s) => s.clearAll)

  const autoGhostRef = useRef<number | null>(null)

  // Clear stale store state on mount — must be defined BEFORE auto-ghost effect
  // so clearAll runs first even when task data is already in React Query cache
  useEffect(() => {
    clearAll()
    autoGhostRef.current = null
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-ghost when rescheduling a task that already has a timed plannedStart
  useEffect(() => {
    if (!task?.plannedStart || !task.assignedTechnician) return
    if (isMidnightScheduledDate(task.plannedStart)) return
    if (autoGhostRef.current === task.id) return
    autoGhostRef.current = task.id

    const workStart = new Date(task.plannedStart)
    const anfahrtStartIso = new Date(
      workStart.getTime() - task.plannedTravelDuration * 60_000,
    ).toISOString()

    const durations: DefaultDurations = {
      plannedTravelDuration: task.plannedTravelDuration || DEFAULT_DURATIONS.plannedTravelDuration,
      plannedWorkDuration: task.plannedWorkDuration || DEFAULT_DURATIONS.plannedWorkDuration,
      plannedDepartureDuration: task.plannedDepartureDuration || DEFAULT_DURATIONS.plannedDepartureDuration,
    }

    // If no day param yet, navigate the timeline to the task's scheduled date
    if (!dayKey) {
      const d = new Date(task.plannedStart)
      setDayKey(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      )
    }

    setDurations(durations)
    setGhost({ technicianId: String(task.assignedTechnician.id), anfahrtStartIso, committed: true })
    finalizeDraft({
      technicianId: String(task.assignedTechnician.id),
      technicianName: task.assignedTechnician.fullName ?? null,
      anfahrtStartIso,
      durations,
      workOrderId: '',
      tradeCode: 0,
    })
  }, [task, dayKey, setDayKey, setDurations, setGhost, finalizeDraft])

  const handleDraftFinalized = (
    technicianId: string,
    technicianName: string | null,
    anfahrtStartIso: string,
    durations: DefaultDurations,
  ) => {
    finalizeDraft({
      technicianId,
      technicianName,
      anfahrtStartIso,
      durations,
      workOrderId: '',
      tradeCode: 0,
    })
  }

  const handleSave = async () => {
    const currentDraft = useDispositionDraftStore.getState().draft
    if (!currentDraft) return

    const workStartIso = new Date(
      new Date(currentDraft.date).getTime() + currentDraft.plannedTravelDuration * 60_000,
    ).toISOString()

    await updateTask.mutateAsync({
      plannedStart: workStartIso,
      assignedTechnicianId: currentDraft.assignedTechnicianId,
      plannedTravelDuration: currentDraft.plannedTravelDuration,
      plannedWorkDuration: currentDraft.plannedWorkDuration,
      plannedDepartureDuration: currentDraft.plannedDepartureDuration,
    })

    clearAll()
    showSuccess('Aufgabe erfolgreich geplant', () => navigate(routes.aufgaben.edit(taskId)))
  }

  const handleCancel = () => {
    clearAll()
    navigate(routes.aufgaben.edit(taskId))
  }

  const handleDayClick = (dayKey: string) => {
    const d = new Date(dayKey + 'T00:00:00')
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    setDayKey(`${y}-${m}-${dd}`)
    setView('day')
  }

  const isReschedule =
    !!task?.plannedStart && !isMidnightScheduledDate(task.plannedStart)

  const pageTitle = isReschedule ? 'Aufgabe umdisponieren' : 'Aufgabe planen'
  const taskDurations: DefaultDurations = useMemo(
    () =>
      task
        ? {
            plannedTravelDuration: task.plannedTravelDuration || DEFAULT_DURATIONS.plannedTravelDuration,
            plannedWorkDuration: task.plannedWorkDuration || DEFAULT_DURATIONS.plannedWorkDuration,
            plannedDepartureDuration: task.plannedDepartureDuration || DEFAULT_DURATIONS.plannedDepartureDuration,
          }
        : DEFAULT_DURATIONS,
    [task],
  )

  return (
    <>
      <PageLayout pageTitle={pageTitle} headerChildren={<TimelineNav />}>
        {view === 'day' ? (
          <DispositionConfigProvider value={{
            workOrderId: '',
            defaultDurations: taskDurations,
            ghostColor: TASK_COLORS.work,
            onDraftFinalized: handleDraftFinalized,
          }}>
            <TimeLineWidget editingTaskId={isReschedule ? taskId : undefined} />
          </DispositionConfigProvider>
        ) : (
          <TimeLineWeekWidget onDayClick={handleDayClick} />
        )}

        {draft && task && (
          <DraftTaskPanel
            taskTitle={task.title}
            taskId={taskId}
            onSave={handleSave}
            onCancel={handleCancel}
            isPending={updateTask.isPending}
          />
        )}
      </PageLayout>

      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => {
          if (!open) dialogProps.onClose()
        }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
    </>
  )
}
